import { debounce, get } from 'lodash'

import { DateFields, Services, getServiceNameViaPoint, getString, joinURLPath } from '@sigma-frontend-libs/commons'
import { isUUID } from '@sigmasms/commons'
import { EUrlShortenerSettingsDomainsStates, EUrlShortenerSettingsDomainsUsersAllowedScopes, OrderDirections } from '@sigmasms/shared-types'

import { globalErrorHandler, permissionPresets, request } from '@/utils'

const maxLimit = 100

const ensureArray = value => {
  if (Array.isArray(value)) {
    return value
  }

  return []
}

export const domainsUsers = {
  props: {
    prependItems: {
      type: Array,
      default: () => {
        return []
      }
    },

    valueKey: {
      type: String,
      default: 'id'
    }
  },

  data() {
    return {
      loading: false,
      search: undefined,
      domainsUsers: []
    }
  },

  computed: {
    $_userId() {
      return this.userId ?? this.account?.id
    },

    canGetDomains() {
      return this.checkPermissions(`advanced.${getServiceNameViaPoint(Services['url-shortener-settings/domains'])}.get`, permissionPresets.meUp)
    },

    activeDomains() {
      if (!Array.isArray(this.domainsUsers)) {
        return []
      }

      return this.domainsUsers.filter(item => {
        if (item?.domain?.state === EUrlShortenerSettingsDomainsStates.active) {
          return true
        }

        return false
      })
    },

    mergedItems() {
      const result = []
      if (Array.isArray(this.prependItems) && this.prependItems.length) {
        result.push(...this.prependItems)
      }
      if (Array.isArray(this.domainsUsers) && this.domainsUsers.length) {
        result.push(...this.domainsUsers)
      }

      return result
    },
    selectItems() {
      const domainIds = new Set()
      const uniqueItems = this.mergedItems.filter(item => {
        const domainId = get(item, 'domain.id')
        if (domainId === undefined) {
          return true
        }
        if (domainIds.has(domainId)) {
          return false
        }
        domainIds.add(domainId)

        return true
      })

      return uniqueItems.map(item => {
        const title = get(item, 'domain.name', getString(item))
        const value = get(item, `domain.${this.valueKey}`, item)
        const state = get(item, 'domain.state')
        const disabled = state !== undefined && state !== EUrlShortenerSettingsDomainsStates.active

        return {
          title,
          value,
          state,
          disabled
        }
      })
    },

    hasActiveDomains() {
      return this.activeDomains.length > 0
    }
  },

  watch: {
    $_userId() {
      this.getDomainsByUser()
    },

    search() {
      this.debouncedGetDomainsByUser()
    },

    hasActiveDomains(value) {
      this.$emit('hasActiveDomains', value)
    }
  },

  async mounted() {
    await this.getDomainsByUser()
    this.$emit('hasActiveDomains', this.hasActiveDomains)
  },

  methods: {
    async fetchDomainsUsersChunk({ domainId, offset } = {}) {
      const pathToApi = joinURLPath(
        'n',
        Services['url-shortener-settings'],
        'domains-users',
        'by-user'
      )
      const query = {
        order: [ [ DateFields.createdAt, OrderDirections.DESC ] ],
        scopes: [ EUrlShortenerSettingsDomainsUsersAllowedScopes.domain ],
        limit: maxLimit
      }
      if (domainId && isUUID(domainId)) {
        query.domainId = domainId
      }
      if (offset !== undefined) {
        query.offset = offset
      }
      if (this.search) {
        query.search = this.search
      }

      const response = await request.get(joinURLPath(pathToApi, this.$_userId), undefined, query)

      return response
    },

    async loadAllDomainsUsers() {
      const result = []

      let offset = 0
      let total
      let hasMore = true

      while (hasMore) {
        const responseData = await this.fetchDomainsUsersChunk({ offset })
        const chunk = ensureArray(responseData?.data)
        if (chunk.length) {
          result.push(...chunk)
        }

        let limit
        if (typeof responseData?.limit === 'number') {
          limit = Math.min(responseData.limit, maxLimit)
        } else {
          limit = maxLimit
        }
        if (typeof responseData?.total === 'number') {
          total = responseData.total
        }
        const received = chunk.length

        if (!received) {
          hasMore = false
        } else if (typeof total === 'number') {
          offset += received
          hasMore = offset < total
        } else {
          offset += received
          hasMore = received === limit
        }
      }

      this.domainsUsers = result

      return result
    },

    async getDomainsByUser(domainId) {
      if (!this.canGetDomains || !this.$_userId) {
        return []
      }

      let result = []
      try {
        this.loading = true
        if (domainId) {
          const responseData = await this.fetchDomainsUsersChunk({ domainId })
          const chunk = ensureArray(responseData?.data)
          if (!Array.isArray(this.domainsUsers)) {
            this.domainsUsers = []
          }
          if (chunk.length) {
            this.domainsUsers.push(...chunk)
          }
          result = chunk
        }

        if (!domainId) {
          const allDomains = await this.loadAllDomainsUsers()
          result = ensureArray(allDomains)
          if (this.value) {
            const domainsUsersHasValue = this.mergedItems.find(item => {
              return item?.domainId === this.value
            })
            if (!domainsUsersHasValue) {
              await this.getDomainsByUser(this.value)
            }
          }
        }
      } catch (error) {
        globalErrorHandler(error)
        result = ensureArray(this.domainsUsers)
      } finally {
        this.loading = false
      }

      return result
    },

    debouncedGetDomainsByUser: debounce(
      async function() {
        await this.getDomainsByUser()
      },
      500,
      {
        leading: false,
        trailing: true
      }
    )
  }
}
